import React, { useState } from "react";
import { GiHorseshoe } from "react-icons/gi";
import { Sparkles } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const ANIMALS = [
  { img: "/horse_full.png", label: "Pferd" },
  { img: "/dog_full.png", label: "Hund" },
  { img: "/cat_full.png", label: "Katze" },
];

const SOUL_GALLERY = [
  { img: "/horse_rappe.png", label: "Pferd" },
  { img: "/dog_aussie.png", label: "Hund" },
  { img: "/cat_fluffy.png", label: "Katze" },
  { img: "/head_pony.png", label: "Pony" },
  { img: "/head_puppy.png", label: "Welpe" },
  { img: "/head_kitten.png", label: "Babykatze" },
  { img: "/head_rabbit.png", label: "Hase" },
  { img: "/head_hamster.png", label: "Hamster" },
  { img: "/head_bird.png", label: "Vogel" },
];

export const StartScreen = ({ onSubmit }) => {
  const [form, setForm] = useState({
    tierart: "",
    name: "",
    alter: "",
    geburtsdatum: "",
    geschlecht: "",
    situation: "",
    verhalten: "",
    symptome: "",
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const req = ["tierart", "name", "alter", "geburtsdatum", "geschlecht", "symptome"];
    const errs = {};
    req.forEach((k) => { if (!form[k]) errs[k] = true; });
    setErrors(errs);
    if (Object.keys(errs).length === 0) onSubmit(form);
    else document.getElementById("analyse-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center px-4 pb-28">
      {/* HERO */}
      <section className="relative w-full max-w-4xl mx-auto pt-10 sm:pt-14 text-center flex flex-col items-center">
        {/* Owner website above the tree */}
        <a
          href="https://samantha-hellsehen-kartenlegen.de"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="owner-website-top"
          className="font-play italic text-base sm:text-lg text-gold hover:underline tracking-wide mb-4"
        >
          samantha-hellsehen-kartenlegen.de
        </a>

        {/* Glowing soul tree */}
        <div className="relative w-52 sm:w-72 mb-2">
          <div className="tree-aura" />
          <img src="/soul_tree.png" alt="Seelenbaum" className="tree-img relative w-full" />
        </div>

        {/* Seelen-Galerie: round glowing animal portraits */}
        <div className="flex flex-wrap items-start justify-center gap-3 sm:gap-4 max-w-5xl mt-4 mb-6">
          {SOUL_GALLERY.map((a, i) => (
            <div
              key={a.label}
              className="flex flex-col items-center fade-up"
              style={{ animationDelay: `${0.1 + i * 0.08}s` }}
            >
              <div className="soul-orb w-[68px] h-[68px] sm:w-20 sm:h-20 float-soft" style={{ animationDelay: `${i * 0.3}s` }}>
                <img src={a.img} alt={a.label} />
              </div>
              <span className="text-[11px] sm:text-xs text-gold mt-1.5 font-play italic">{a.label}</span>
            </div>
          ))}
        </div>

        {/* Subtitle with up-facing horseshoes */}
        <div className="flex items-center justify-center gap-4 sm:gap-7 mb-3 mt-2">
          <GiHorseshoe className="hoof-up text-3xl sm:text-5xl shrink-0" />
          <p
            data-testid="portal-subtitle"
            className="font-play italic text-2xl sm:text-4xl font-medium text-slate-700/90 tracking-wide"
          >
            Ein Portal in die Tierseelenwelt
          </p>
          <GiHorseshoe className="hoof-up text-3xl sm:text-5xl shrink-0" />
        </div>

        {/* Sweeping radiant title */}
        <h1 className="title-script text-6xl sm:text-8xl px-2 pb-2">
          Tier&nbsp;Seelen&nbsp;Analyse
        </h1>

        <div className="flex items-center justify-center gap-3 mt-3 text-slate-500">
          <span className="divider-gold w-10" />
          <p className="font-play text-sm sm:text-base tracking-[0.3em] uppercase text-gold">
            Energie&nbsp;·&nbsp;Gesundheit&nbsp;·&nbsp;Botschaften
          </p>
          <span className="divider-gold w-10" />
        </div>

        <a href="#analyse-form">
          <button
            data-testid="scroll-to-form-btn"
            className="gold-btn mt-10 rounded-full px-12 py-4 font-play text-base uppercase"
          >
            Analyse beginnen
          </button>
        </a>

        {/* Full-body animal trio */}
        <div className="grid grid-cols-3 gap-3 sm:gap-8 w-full mt-10 sm:mt-12">
          {ANIMALS.map((a, i) => (
            <div key={a.label} className="flex flex-col items-center fade-up" style={{ animationDelay: `${0.2 + i * 0.15}s` }}>
              <img
                src={a.img}
                alt={a.label}
                className="animal-elegant w-full max-w-[250px] float-soft"
                style={{ animationDelay: `${i * 0.7}s` }}
              />
              <span className="font-play italic text-base sm:text-xl text-gold -mt-1">{a.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FORM */}
      <form
        id="analyse-form"
        onSubmit={handleSubmit}
        data-testid="analyse-form"
        className="card-elegant w-full max-w-2xl mx-auto mt-20 rounded-[2rem] p-7 sm:p-12 scroll-mt-8 fade-up"
      >
        <div className="text-center mb-9">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full gold-frame mb-4">
            <Sparkles className="text-gold" size={24} />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-800">
            Erzähle mir von deinem Tier
          </h2>
          <p className="text-slate-500 mt-2 text-base">
            Je mehr du teilst, desto tiefer und persönlicher wird die energetische Deutung.
          </p>
          <span className="divider-gold w-24 inline-block mt-6" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Tierart" req error={errors.tierart}>
            <Select value={form.tierart} onValueChange={(v) => set("tierart", v)}>
              <SelectTrigger data-testid="tierart-select" className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60">
                <SelectValue placeholder="Wähle…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Hund">Hund</SelectItem>
                <SelectItem value="Katze">Katze</SelectItem>
                <SelectItem value="Pferd">Pferd</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Name des Tieres" req error={errors.name}>
            <Input data-testid="name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="z. B. Luna" className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60" />
          </Field>

          <Field label="Alter" req error={errors.alter}>
            <Input data-testid="alter-input" value={form.alter} onChange={(e) => set("alter", e.target.value)}
              placeholder="z. B. 4 Jahre" className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60" />
          </Field>

          <Field label="Geburtsdatum" req error={errors.geburtsdatum}>
            <Input data-testid="geburtsdatum-input" type="date" value={form.geburtsdatum}
              onChange={(e) => set("geburtsdatum", e.target.value)} className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60" />
          </Field>

          <Field label="Geschlecht" req error={errors.geschlecht}>
            <Select value={form.geschlecht} onValueChange={(v) => set("geschlecht", v)}>
              <SelectTrigger data-testid="geschlecht-select" className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60">
                <SelectValue placeholder="Wähle…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="männlich">Männlich</SelectItem>
                <SelectItem value="weiblich">Weiblich</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Symptome / Auffälligkeiten" req error={errors.symptome}>
            <Input data-testid="symptome-input" value={form.symptome} onChange={(e) => set("symptome", e.target.value)}
              placeholder="z. B. unruhig, humpelt" className="bg-white/70 rounded-xl h-12 text-base border-amber-200/60" />
          </Field>
        </div>

        <div className="mt-6 space-y-5">
          <Field label="Situation / Frage (optional)">
            <Textarea data-testid="situation-input" value={form.situation} onChange={(e) => set("situation", e.target.value)}
              placeholder="Was beschäftigt dich rund um dein Tier?" className="bg-white/70 rounded-xl min-h-[80px] text-base border-amber-200/60" />
          </Field>
          <Field label="Verhalten des Tieres (optional)">
            <Textarea data-testid="verhalten-input" value={form.verhalten} onChange={(e) => set("verhalten", e.target.value)}
              placeholder="Wie verhält sich dein Tier aktuell?" className="bg-white/70 rounded-xl min-h-[80px] text-base border-amber-200/60" />
          </Field>
        </div>

        <button type="submit" data-testid="submit-analyse-btn"
          className="gold-btn w-full mt-9 rounded-full py-4 font-play text-base uppercase">
          Tieranalyse starten
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, req, error, children }) => (
  <div>
    <Label className={`mb-2 block text-sm font-medium tracking-wide ${error ? "text-red-500" : "text-slate-600"}`}>
      {label} {req && <span className="text-gold">✦</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">Bitte ausfüllen</p>}
  </div>
);
